import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { NavigationItem } from '../../models/layout.model';
import { Message } from '../../models/message.model';
import { LayoutService } from '../../services/layout.service';
import { MessageService } from '../../services/message.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css'],
  animations: [
    trigger('slideInOut', [
      state(
        'in',
        style({
          transform: 'translate3d(0, 0, 0)',
        })
      ),
      state(
        'out',
        style({
          transform: 'translate3d(-100%, 0, 0)',
        })
      ),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out')),
    ]),
  ],
})
export class LayoutComponent extends BaseComponent {
  @ViewChild('toastRef') toast: ElementRef;
  menuState: string = 'out';
  navigationItems: NavigationItem[];
  message: Message;

  constructor(
    messageSerivce: MessageService,
    private activatedRoute: ActivatedRoute,
    private layoutService: LayoutService,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {
    super();

    messageSerivce.hasMessage$.pipe(takeUntil(this.destroy$)).subscribe((it) => {
      if (!it) return;
      this.message = it;
      this.toast.nativeElement.show();
    });

    // used to close the navigation drawer when the screen size is large
    this.breakpointObserver
      .observe([Breakpoints.Large])
      .pipe(
        map((result) => result.matches),
        takeUntil(this.destroy$)
      )
      .subscribe((isDesktopView) => {
        if (isDesktopView) {
          if (this.menuState === 'in') {
            this.closeNavigatioDrawer();
          }
        }
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateSelectedNavigationItemBasedOnRoute();
      });

    this.layoutService.navigationDrawerState$.pipe(takeUntil(this.destroy$)).subscribe((it) => {
      if (it) {
        this.menuState = 'in';
      } else {
        this.menuState = 'out';
      }
    });

    this.layoutService.navigationItem$.pipe(takeUntil(this.destroy$)).subscribe((it) => {
      this.navigationItems = it;
    });
  }

  get isAnyTabSelected(): boolean {
    return this.navigationItems.some((it) => it.selected);
  }

  closeNavigatioDrawer(): void {
    this.layoutService.closeNavigationDrawer();
  }

  handleTabSelectionChange(event: any): void {
    const navigationItemId = event.detail.tab.id;
    const item = this.navigationItems.find((it) => it.id === navigationItemId);

    if (item) {
      this.router.navigate([item.route], { queryParams: item.query });
    } else {
      this.router.navigateByUrl("/settings");
    }
  }

  handleSideNavigationSelectionChange(event: any): void {
    const navigationItemId = event.detail.item.id;
    const item = this.navigationItems
      .filter((it) => it.selected)
      .flatMap((it) => it.children)
      .find((it) => it.id === navigationItemId);

    if (item) {
      this.router.navigate([item.route], { queryParams: item.query });
    } else {
      this.router.navigateByUrl("/settings");
    }
  }

  handleNavigationDrawerSelectionChange(event: any): void {
    const navigationItemId = event.detail.item.id;
    const item = this.navigationItems.find((it) => it.id === navigationItemId);

    if (item) {
      this.navigateAndCloseDrawer(item);
      return;
    }

    const childItem = this.navigationItems.flatMap((it) => it.children).find((it) => it.id === navigationItemId);

    if (childItem) {
      this.navigateAndCloseDrawer(childItem);
    }
  }

  private navigateAndCloseDrawer(item: NavigationItem): void {
    if (!item.route) return;
    this.layoutService.selectNavigationItem(item);
    this.closeNavigatioDrawer();
    this.router.navigate([item.route], { queryParams: item.query });
  }

  private updateSelectedNavigationItemBasedOnRoute(): void {
    const currentRoute = this.router.url.split('?')[0];
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const navItem = this.navigationItems.find((item) => item.route === currentRoute);

    if (navItem) {
      if (queryParams && queryParams.child) {
        const navItemChild = navItem.children.find((child) => child.query.child === queryParams.child);
        navItemChild && this.layoutService.selectNavigationItem(navItemChild);
      } else {
        this.layoutService.selectNavigationItem(navItem);
        this.router.navigate([navItem.route], { queryParams: navItem.query });
      }
    } else {
      this.layoutService.selectNavigationItem();
    }
  }
}
